import range from 'zycommon-web/utils/range';

/**
 * Gets the keybindings for the Propositional Logic virtual keyboard.
 * @param {SymbolicShortAnswerQuestion} symbolicQuestion The symbolic short answer question containing the necessary tool-specific properties.
 * @param {boolean} symbolicQuestion.support_true_and_false The support for true and false in the question.
 * @param {Array} editorAtomicSymbols The atomic symbols used in the editor.
 * @returns {Array} The keybindings for the Propositional Logic virtual keyboard.
 */
export function getPropositionalLogicKeybindings(symbolicQuestion, editorAtomicSymbols) {
    const { support_true_and_false } = symbolicQuestion;
    const keybindings = [];

    keybindings.push(
        { key: '^', command: ['insert', '\\land'] },
        { key: 'v', command: ['insert', '\\lor'] },
        ...editorAtomicSymbols.filter(({ key }) => key).map(({ key, macro }) => ({ key, command: ['insert', macro] })),
    );

    if (support_true_and_false) {
        keybindings.push(
            { key: 'T', command: ['insert', '\\True'] },
            { key: 't', command: ['insert', '\\True'] },
            { key: 'F', command: ['insert', '\\False'] },
            { key: 'f', command: ['insert', '\\False'] },
        );
    }

    return keybindings;
}

/**
 * The Propositional Logic virtual keyboard layout.
 * @param {Object} commonKeys The common keys used across different virtual keyboard layouts.
 * @param {SymbolicShortAnswerQuestion} symbolicQuestion The symbolic short answer question containing the necessary tool-specific properties.
 * @param {Array} symbolicQuestion.boolean_functions The boolean functions used in the question.
 * @param {Array} symbolicQuestion.boolean_variables The boolean variables used in the question.
 * @param {Array} symbolicQuestion.operators The operators used in the question.
 * @param {boolean} symbolicQuestion.support_true_and_false The support for true and false in the question.
 * @param {Array} symbolicQuestion.untyped_variables The untyped variables used in the question.
 * @param {Array} editorAtomicSymbols The atomic symbols used in the editor.
 * @returns {Array} The keyboard layout.
 */
export function getPropositionalLogicKeyboardLayout(commonKeys, symbolicQuestion, editorAtomicSymbols) {
    const { deleteKeyCap, moveLeftKeyCap, moveRightKeyCap, nextStepKeyCap, separator } = commonKeys;
    const { boolean_functions, boolean_variables, operators, support_true_and_false, untyped_variables } = symbolicQuestion;
    const rows = [];

    const customKeys = editorAtomicSymbols.map(({ key }) => key);
    const functionNamesToInclude = boolean_functions.filter(name => !customKeys.includes(name));
    const longLatex = 'P(x,y,z)'.length;
    const customVariables = editorAtomicSymbols.map(({ latex, macro }) => ({
        class: latex.replace(' ', '').length >= longLatex ? 'small' : '',
        insert: macro,
        latex,
    }));

    // Add True and False keys
    if (support_true_and_false) {
        customVariables.push({ insert: '\\True', latex: '\\text{T}' }, { insert: '\\False', latex: '\\text{F}' });
    }

    const controlKeys = [moveLeftKeyCap, moveRightKeyCap, deleteKeyCap, nextStepKeyCap];
    const otherVariables = [...boolean_variables, ...untyped_variables, ...functionNamesToInclude];
    const hasQuantifier = ['\\exists', '\\forall'].some(quantifier => operators.includes(quantifier));
    const userMayNeedComma = hasQuantifier && functionNamesToInclude.length ? [','] : [];
    const operatorKeys = [...operators, '(', ')', ...userMayNeedComma];
    const variableKeys = otherVariables.concat(customVariables);

    // Determine optimal number of keys per row, either with a modulo of 0 or maximizing the modulo value.
    // The modulus corresponds to the number of keys in the last row of the virtual keyboard for that numKeysPerRow. A modulus of 0 represents an equal number of keys per row, and is preferred.
    // The goal is to avoid the last row having only a few keys, which would make the virtual keyboard look unbalanced.
    // Ex:
    // For 8 keys, 8 % 4 = 0, and 8 % 5 = 3, so 4 keys are used. (2 rows of 4)
    // For 9 keys, 9 % 4 = 1, and 9 % 5 = 4, so 5 keys are used. (1 row of 5, 1 row of 4)
    const optimizeNumKeysPerRow = numElements => {
        // eslint-disable-next-line no-magic-numbers
        const potentialKeys = [4, 5];

        const moduli = potentialKeys.map(keysPerRow => numElements % keysPerRow);
        const exactMatchIndex = moduli.findIndex(modulus => modulus === 0);

        if (exactMatchIndex !== -1) {
            return potentialKeys[exactMatchIndex];
        }

        // Choose the one that results in the greatest modulo, preferring the first one in case of a tie
        const bestMatchIndex = moduli.indexOf(Math.max(...moduli));

        return potentialKeys[bestMatchIndex];
    };

    const variableKeysPerRow = optimizeNumKeysPerRow(variableKeys.length);
    const operatorKeysPerRow = optimizeNumKeysPerRow(operatorKeys.length);
    const maxKeysPerRow = Math.max(variableKeysPerRow, operatorKeysPerRow);

    // Generate the virtual keyboard layout for the given keys.
    const createLayout = (keys, keysPerRow) =>
        range(Math.ceil(keys.length / keysPerRow)).map((_, rowIndex) =>
            range(maxKeysPerRow).map(colIndex => {
                const targetIndex = rowIndex * keysPerRow + colIndex;

                // A valid index is less than keysPerRow for that given row (Ex: 0-3, 4-7, etc.) and < keys.length
                const targetIndexIsValid = targetIndex < (rowIndex + 1) * keysPerRow && targetIndex < keys.length;

                return targetIndexIsValid ? keys[targetIndex] : separator;
            }),
        );

    const variableKeysLayout = createLayout(variableKeys, variableKeysPerRow);
    const operatorKeysLayout = createLayout(operatorKeys, operatorKeysPerRow);

    // If necessary, pad control keys with separators to maxKeysPerRow
    while (controlKeys.length < maxKeysPerRow) {
        controlKeys.push(separator);
    }

    rows.push(...variableKeysLayout, ...operatorKeysLayout, controlKeys);

    return rows;
}

/**
 * Get the Propositional Logic macros for the math live editor.
 * @param {Object} currentMacros The current macros for the math live editor.
 * @param {Array} editorAtomicSymbols The atomic symbols used in the editor.
 * @returns {Object} The functions macros.
 */
export function getPropositionalLogicMacros(currentMacros, editorAtomicSymbols) {
    return {
        ...currentMacros,
        ...Object.fromEntries(editorAtomicSymbols.map(({ latex, macro }) => [macro.substring(1), latex])),
        True: '\\text{T}',
        False: '\\text{F}',
    };
}
